import { createRouter, createWebHistory } from 'vue-router'
import { getToken } from '@/utils/auth.js'
import { getMenusByUser } from '@/api/admin/menu.js'
import _ from 'lodash'
import store from '@/store'
import { treeToList } from '@/utils/util.js'
import { getUserRole } from '@/api/auth.js'
import i18n from '@/i18n'

const SUPPORTED_LANGS = ['zh', 'en', 'ja', 'ko', 'zh-TW']

function setLangAndNext(to, next, titleKey) {
  const lang = to.params.lang
  if (!SUPPORTED_LANGS.includes(lang)) return next('/zh')
  i18n.global.locale.value = lang
  localStorage.setItem('lang', lang)
  const langMap = { 'zh': 'zh-CN', 'en': 'en', 'ja': 'ja', 'ko': 'ko', 'zh-TW': 'zh-TW' }
  document.documentElement.lang = langMap[lang] || 'en'
  if (titleKey) {
    document.title = i18n.global.t(titleKey)
  }
  next()
}

const routes = [
  {
    path: '/',
    redirect: () => {
      const browserLang = navigator.language || navigator.userLanguage
      let lang = 'en'
      if (browserLang.startsWith('zh')) {
        lang = (browserLang === 'zh-TW' || browserLang === 'zh-Hant') ? 'zh-TW' : 'zh'
      } else if (browserLang.startsWith('ja')) {
        lang = 'ja'
      } else if (browserLang.startsWith('ko')) {
        lang = 'ko'
      }
      return `/${lang}`
    },
    meta: { isWhite: true }
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)',
    name: 'Landing',
    component: () => import('@/views/Landing'),
    meta: { isWhite: true },
    beforeEnter: (to, from, next) => setLangAndNext(to, next, 'seo.landing.title')
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)/MangaTranslation',
    name: 'MangaTranslation',
    component: () => import('@/views/manga/MangaTranslation'),
    meta: { isWhite: true },
    beforeEnter: (to, from, next) => setLangAndNext(to, next, 'seo.translate.title')
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)/vip',
    name: 'MangaTranslationVip',
    component: () => import('@/views/manga/MangaTranslationVip'),
    meta: { isWhite: true },
    beforeEnter: (to, from, next) => setLangAndNext(to, next, 'seo.vip.title')
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)/login',
    name: 'Login',
    component: () => import('@/views/login'),
    meta: { isWhite: true },
    beforeEnter: (to, from, next) => setLangAndNext(to, next)
  },
  {
    path: '/:lang(zh-TW|zh|en|ja|ko)/resetPassword',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPassword'),
    meta: { isWhite: true },
    beforeEnter: (to, from, next) => setLangAndNext(to, next)
  },
  {
    path: '/403',
    name: '403',
    component: () => import('@/views/403'),
    meta: { isWhite: true }
  },
  // PayPal 回调路由 (暂时隐藏，后续启用)
  // {
  //   path: '/paypal-return',
  //   name: 'PayPalReturn',
  //   component: () => import('@/views/PayPalReturn'),
  //   meta: { isWhite: true }
  // },
  {
    path: '/404',
    name: '404',
    component: () => import('@/views/404'),
    meta: { isWhite: true }
  },
  {
    path: '/subscription',
    component: () => import('@/components/subscription/SubscriptionPlans.vue'),
    meta: { isWhite: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, from, next) => {
  const tokenStr = getToken()
  let target = to.path.replace(/\/+$/, '') || '/'
  const whiteListPaths = getWhiteListPaths()

  if (!_.isEmpty(to.meta.parentUrl)) {
    target = to.meta.parentUrl
  }

  const isWhiteListed = whiteListPaths.some((path) => {
    if (path.includes(':')) {
      const pattern = new RegExp('^' + path.replace(/:[^/]+/g, '[^/]+') + '$')
      return pattern.test(target)
    }
    return path === target
  })

  if (isWhiteListed) {
    next()
  } else {
    if (!tokenStr) {
      next('/404')
    } else {
      await store.commit('setUserInfo')
      await store.commit('setUserRole')
      const paths = getPaths()
      const userPaths = await getUserPaths()

      const hasPathAccess = paths.some((path) => {
        if (path.includes(':')) {
          const pattern = new RegExp('^' + path.replace(/:[^/]+/g, '[^/]+') + '$')
          return pattern.test(target)
        }
        return path === target
      })

      const hasUserPathAccess = userPaths.some((path) => {
        if (path.includes(':')) {
          const pattern = new RegExp('^' + path.replace(/:[^/]+/g, '[^/]+') + '$')
          return pattern.test(target)
        }
        return path === target
      })

      if (hasPathAccess && hasUserPathAccess) {
        next()
      } else if (hasPathAccess && !hasUserPathAccess) {
        next('/403')
      } else {
        next('/404')
      }
    }
  }
})

function getWhiteListPaths() {
  const whiteListPaths = []
  treeToList(routes, (route) => {
    if (route.meta && route.meta.isWhite) {
      whiteListPaths.push(route.path)
    }
  })
  return whiteListPaths
}

function getPaths() {
  const paths = []
  treeToList(routes, (route) => {
    paths.push(route.path)
  })
  return paths
}

async function getUserPaths() {
  try {
    const { data } = await getMenusByUser()
    return data.filter((menu) => menu.url).map((menu) => menu.url)
  } catch (error) {
    console.error('获取用户菜单失败:', error)
    return []
  }
}

export default router
